import { addSelectedFiltersToQuery } from 'src/utils/utils';
import { request } from '../api';

export type DeviceListCounts = { alarms: { total: number; byCriticality: Record<string, number> }; workOrders: number };

export interface DeviceListCircuit extends DeviceListCounts {
  id: string;
  tag: string;
  description: string | null;
  model: string | null;
  unit: string | null;
  offline: boolean;
  forcedOn: boolean;
  loto: boolean;
}

export interface DeviceListItem extends DeviceListCounts {
  id: string;
  kind: 'controller' | 'standaloneCircuit';
  tag: string;
  description: string | null;
  model: string | null;
  unit: string | null;
  panel: { id: string; tag: string } | null;
  commLoop: { id: string; tag: string } | null;
  group?: { id: string; name: string };
  offline: boolean;
  forcedOn: boolean;
  loto: boolean;
  matched: boolean;
  circuitCount: number;
  rollup: DeviceListCounts;
  circuits: DeviceListCircuit[];
}

/** Device count per filter chip, keyed by its attribute. Severity chips are
 * `'alarm:<criticality>'` (the `eht_alarms.critical` level), e.g. `{ offline: 17, 'alarm:3': 1 }`. */
export type DeviceListFilterCounts = Record<string, number>;

/** The devices matching the filters, by type. */
export interface DeviceListSummary {
  devices: number;
  controllers: number;
  circuits: number;
}

export interface DeviceListResponse {
  data: DeviceListItem[];
  /** Top-level rows; pages are counted in these. (ie controllers) */
  total: number;
  deviceCount?: number;
  summary?: DeviceListSummary;
  filterCounts?: DeviceListFilterCounts;
  offset: number;
  nextOffset: number;
  hasMore: boolean;
}

export interface DeviceListScope {
  panelId?: string;
  commLoopId?: string;
  groupId?: string;
  groupBy?: 'group' | 'connection';
  controllerId?: string;
}

const SCOPE_KEYS = ['panelId', 'commLoopId', 'groupId', 'groupBy', 'controllerId'] as const;

export const deviceListScopeKey = (scope?: DeviceListScope): string =>
  SCOPE_KEYS.map((key) => scope?.[key] ?? '').join('|');

export const deviceListItemKey = (item: Pick<DeviceListItem, 'id' | 'group'>): string =>
  item.group ? `${item.group.id}:${item.id}` : item.id;

export interface DeviceListQuery {
  selectedFilters: any;
  sort: string;
  scope?: DeviceListScope;
  search?: string;
  countKeys?: string[];
}

const PAGE_SIZE = 25;

/* What narrows the list: header filters, status chips, sidebar scope and search. The export sends the same. */
function deviceListFilterQuery ({ selectedFilters, scope, search }: Pick<DeviceListQuery, 'selectedFilters' | 'scope' | 'search'>, query: any = {}) {
  addSelectedFiltersToQuery(selectedFilters, query);
  SCOPE_KEYS.forEach((key) => {
    if (scope?.[key]) query[key] = scope[key];
  });
  if (search) {
    query.search = search;
  }
  return query;
}

export const getDeviceList = (action, { sort, countKeys, ...filters }: DeviceListQuery, offset: number): Promise<DeviceListResponse> => {
  const query: any = deviceListFilterQuery(filters, { sort, offset, limit: PAGE_SIZE });
  if (offset === 0 && countKeys?.length) {
    query.count = countKeys.join(',');
  }

  return request(action, {
    payload: {
      url: '/eht-controllers/list',
      query,
      options: { method: 'GET' }
    },
    normalize: (data) => data.toJS()
  });
};

export const getDeviceListExport = (
  action,
  filters: Pick<DeviceListQuery, 'selectedFilters' | 'scope' | 'search'>,
  signal?: AbortSignal
): Promise<string> =>
  request(action, {
    payload: {
      url: '/eht-controllers/list/export',
      query: deviceListFilterQuery(filters),
      options: { method: 'GET', signal }
    }
  });
